import React, {useState} from "react"
import { useAppSelector, useAppDispatch } from "../hooks/redux-hooks" 
import { 
	selectCurrentTicketId,
	setBoard,
} from "../slices/boardSlice"
import {
	toggleShowModal, 
	setModalType, 
	setModalProps, 
} from '../slices/modalSlice'
import { Ticket } from "./Ticket"
import "../styles/board.css"
import { ToolBar } from "./boards/ToolBar" 
import { v4 as uuidv4 } from "uuid" 
import type { 
	Cell as CellType, 
	Status, 
	Ticket as TicketType, 
	Board as BoardType, 
	KanbanBoard as KanbanBoardType,
	Priority,
	LoadingStatus
} from "../types/common"
import { prioritySort as sortByPriority, sortStatusByOrder } from "../helpers/functions" 
import { useDeleteBoardStatusMutation } from "../services/private/board"
import { addToast } from "../slices/toastSlice"
import { boardGroupBy } from "../helpers/groupBy"
import { GroupedBoard } from "./boards/GroupedBoard"
import { Board as DefaultBoard } from "./boards/Board"
import { useScreenSize } from "../hooks/useScreenSize"
import { LG_BREAKPOINT, XL_BREAKPOINT, MD_BREAKPOINT } from "../helpers/constants"

export const Board = () => {
	const {board, boardInfo, filteredTickets, tickets, statusesToDisplay, groupBy} = useAppSelector((state) => state.board)
	const { statuses: allStatuses } = useAppSelector((state) => state.status)
	const { priorities } = useAppSelector((state) => state.priority)
	const [ deleteBoardStatus, {isLoading: hideStatusHandlerLoading, error} ] = useDeleteBoardStatusMutation()
	const dispatch = useAppDispatch()
	const {width, height} = useScreenSize()
	const boardStyle = {
		"maxWidth": `${width}px`,
		"overflowX": "auto",
		"display": "grid",	
		"gridTemplateColumns": `repeat(${statusesToDisplay.length}, minmax(200px, 1fr))`,
		"gridGap": "8px",
		"width": "100%"
	}
	const [loadingStatus, setLoadingStatus] = useState<LoadingStatus>({id: 0, isLoading: false})

	const prioritySort = (sortOrder: 1 | -1, statusId: number | undefined) => {
		let sortedBoard = sortByPriority(
			board,		
			tickets,
			priorities,
			sortOrder,
			statusId,
		)
		dispatch(setBoard(sortedBoard))
	}

	const dragStart = (e: React.DragEvent<HTMLDivElement>) => {
		e.dataTransfer.setData("text", e.currentTarget.id)
	}

	const enableDropping = (e: React.DragEvent<HTMLDivElement>) => {
		/* 
			Because of the side scroll that appears on smaller screens, 
			disabling ticket dragging and movement for smaller screens
		*/
		if (width >= LG_BREAKPOINT){
			e.preventDefault()
		}
	}

	const addTicketHandler = (statusId: number) => {
		dispatch(toggleShowModal(true))
		dispatch(setModalType("ADD_TICKET_FORM"))
		dispatch(setModalProps({statusId: statusId, statusesToDisplay, boardId: boardInfo?.id}))
	}

	const hideStatusHandler = async (statusId: number) => {
		setLoadingStatus({id: statusId, isLoading: true})	
		if (boardInfo?.id && statusId){
			try {
				await deleteBoardStatus({statusId, boardId: boardInfo.id}).unwrap()
				dispatch(addToast({
	    			id: uuidv4(),
	    			type: "success",
	    			animationType: "animation-in",
	    			message: `Status hidden successfully!`,
	    		}))
			}
			catch {
				dispatch(addToast({
	    			id: uuidv4(),
	    			type: "failure",
	    			animationType: "animation-in",
	    			message: `Failed to hide status.`,
	    		}))	
			}
		}
		else {
			dispatch(addToast({
    			id: uuidv4(),
    			type: "failure",
    			animationType: "animation-in",
    			message: `Failed to hide status.`,
    		}))		
		}
		setLoadingStatus({id: statusId, isLoading: false})	
	}

	return (
		<div className = "tw-flex tw-flex-col">
			<ToolBar/>
			{
				groupBy !== "NONE" ? (
					/* Dragging tickets is disabled on mobile */
					<GroupedBoard
						groupedTickets={boardGroupBy(groupBy, tickets, statusesToDisplay)}
						board={board}
						boardId={boardInfo?.id ?? 0}
						boardStyle={boardStyle}
						groupBy={groupBy}
						dragStart={dragStart}
						enableDropping={enableDropping}
						tickets={filteredTickets}
						statusesToDisplay={statusesToDisplay}
						colWidth={width/statusesToDisplay.length}
						allStatuses={allStatuses}
						addTicketHandler={addTicketHandler}
						hideStatusHandler={hideStatusHandler}
						hideStatusHandlerLoading={loadingStatus}
					/>
				) : (
					/* Dragging tickets is disabled on mobile */
					<DefaultBoard
						enableDropping={enableDropping}
						board={board}
						boardId={boardInfo?.id ?? 0}
						boardStyle={boardStyle}
						dragStart={dragStart}
						tickets={filteredTickets}
						statusesToDisplay={statusesToDisplay}
						allStatuses={allStatuses}
						colWidth={width/statusesToDisplay.length}
						addTicketHandler={addTicketHandler}
						hideStatusHandler={hideStatusHandler}
						hideStatusHandlerLoading={loadingStatus}
					/>		
				)
			}
		</div>
	)	
}
